import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Upload, Search, Trash2, Brain, X } from "lucide-react";

export default function MRIScans() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [preview, setPreview] = useState("");
  const [viewer, setViewer] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["mri_scans", q],
    queryFn: async () => (await api.get(`/mri_scans?q=${encodeURIComponent(q)}`)).data,
  });

  const saveMut = useMutation({
    mutationFn: async (payload) => api.post("/mri_scans", payload),
    onSuccess: () => { toast.success("MRI scan uploaded"); qc.invalidateQueries({ queryKey: ["mri_scans"] }); setOpen(false); setForm({}); setPreview(""); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/mri_scans/${id}`),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["mri_scans"] }); },
  });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setPreview(reader.result); setForm((s) => ({ ...s, title: s.title || f.name })); };
    reader.readAsDataURL(f);
  };

  const submit = () => {
    if (!form.title?.trim()) return toast.error("Title is required");
    if (!preview) return toast.error("Please choose an image");
    saveMut.mutate({ ...form, image: preview });
  };

  const items = data || [];

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">MRI Scans</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Upload and store MRI scan images.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="upload-mri-btn"><Upload className="h-4 w-4 mr-2" />Upload Scan</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Upload MRI Scan</DialogTitle></DialogHeader>
            <div className="space-y-4 py-1">
              <div className="border-2 border-dashed border-border rounded-lg p-5 text-center">
                {preview ? <img src={preview} alt="preview" className="max-h-44 mx-auto rounded" /> : <Brain className="h-10 w-10 mx-auto text-muted-foreground" />}
                <Input type="file" accept="image/*" onChange={onFile} className="mt-3" data-testid="mri-file" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Title</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="mri-title" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Subject / ID</Label><Input value={form.subject_id || ""} onChange={(e) => setForm({ ...form, subject_id: e.target.value })} data-testid="mri-subject" /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="mri-notes" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} disabled={saveMut.isPending} data-testid="mri-save">{saveMut.isPending ? "Uploading…" : "Upload"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search scans…" className="pl-9" data-testid="mri-search" />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="aspect-square rounded-lg" />)}</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-16 text-center text-sm text-muted-foreground">No scans uploaded yet.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((m) => (
            <div key={m.id} className="border border-border rounded-lg overflow-hidden bg-card group" data-testid={`mri-card-${m.id}`}>
              <button onClick={() => setViewer(m)} className="block w-full aspect-square bg-muted overflow-hidden">
                <img src={m.image} alt={m.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              </button>
              <div className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{m.title}</div>
                    {m.subject_id && <div className="text-xs text-muted-foreground truncate">ID: {m.subject_id}</div>}
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7 text-destructive shrink-0" data-testid={`mri-delete-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete scan?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => delMut.mutate(m.id)} data-testid="mri-confirm-delete">Delete</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {viewer && (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" data-testid="mri-viewer">
          <div className="flex items-center justify-between p-4 text-white">
            <div><div className="font-medium">{viewer.title}</div><div className="text-xs text-white/60">{viewer.subject_id ? `ID: ${viewer.subject_id}` : ""} {viewer.notes || ""}</div></div>
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" onClick={() => setViewer(null)} data-testid="mri-viewer-close"><X className="h-5 w-5" /></Button>
          </div>
          <div className="flex-1 flex items-center justify-center p-6 overflow-auto"><img src={viewer.image} alt={viewer.title} className="max-w-full max-h-full" /></div>
        </div>
      )}
    </div>
  );
}
